import React, { useState } from 'react';
import { resetAccountProgress } from '../lib/localAccount';
import { billingApi } from '../lib/billingApi';

export default function SettingsModal({
  authUser,
  canManage,
  subscription,
  onSignOut,
  onEditNutrition,
  theme,
  setShowSettingsModal,
  isDeveloper,
  setShowAdminDB,
  setShowOnboardingEdit,
  isPro,
  isTrialActive,
  trialDaysLeft,
  isDark,
  setShowPaywallModal,
  handleSaveProfileSettings,
  editProfile,
  setEditProfile,
  calculateScienceMacros,
  checkMacroContradictions,
  handleRecalculateMacrosClick,
  AVAILABLE_EQUIPMENT_OPTIONS,
  handleEquipmentToggle,
  userProfile
}) {
  const [signingOut, setSigningOut] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState('');
  const openPortal = async () => {
    if (portalBusy) return;
    setPortalBusy(true); setPortalError('');
    try {
      const result = await billingApi('/api/billing/portal', {});
      const url = new URL(result.url);
      if (url.protocol !== 'https:' || url.hostname !== 'billing.stripe.com') throw new Error('La dirección de gestión no es válida.');
      window.location.assign(url.href);
    } catch (error) { setPortalError(error.message); setPortalBusy(false); }
  };
  const closeSession = async () => {
    if (signingOut) return;
    setSigningOut(true); setSessionError('');
    try { await onSignOut(); }
    catch { setSessionError('No se pudo cerrar la sesión. Comprueba la conexión e inténtalo de nuevo.'); setSigningOut(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-6 animate-fadeIn">
      <div className={`${theme.card} border ${theme.border} rounded-[2.5rem] p-8 max-w-sm w-full space-y-6 max-h-[85vh] overflow-y-auto shadow-2xl`}>
        <div className={`flex justify-between items-center border-b ${theme.border} pb-4`}>
          <h3 className={`text-xl font-black ${theme.text}`}>Mi perfil</h3>
          <button 
            onClick={() => setShowSettingsModal(false)} 
            className={`${theme.muted} font-bold text-xl hover:text-red-500 transition-colors`}
          >
            ✕
          </button>
        </div>

        {/* ACCESO A BASE DE DATOS DE DESARROLLADOR */}
        {isDeveloper && (
          <button 
            onClick={() => { setShowSettingsModal(false); setShowAdminDB(true); }} 
            className="w-full py-4 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-400 font-black text-xs uppercase tracking-widest rounded-full shadow-lg transition-all flex items-center justify-center gap-2 active:scale-95"
          >
            <span>🗄️</span> Admin: Base de Datos de Ejercicios
          </button>
        )}

        <button 
          type="button"
          onClick={() => { setShowSettingsModal(false); setShowOnboardingEdit(true); }}
          className="w-full py-4 bg-emerald-600 text-white font-black text-xs uppercase tracking-widest rounded-full shadow-lg hover:bg-emerald-700 transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          <span>✎</span> Datos y entrenamiento
        </button>

        <button type="button" onClick={onEditNutrition} className={`w-full py-4 rounded-full font-black text-xs ${theme.primary}`}>Editar mis preferencias de nutrición</button>
        {/* ESTADO DE LA SUSCRIPCIÓN */}
        <div className={`p-5 rounded-3xl border border-blue-500/30 bg-blue-500/10 space-y-3`}>
          <div className="flex justify-between items-center">
            <span className="text-xs font-black text-blue-500 uppercase tracking-widest">Plan Actual</span>
            <span className={`text-[10px] px-3 py-1 rounded-full font-bold uppercase ${isDeveloper ? 'bg-purple-500 text-white' : isPro ? 'bg-green-500 text-black' : isTrialActive ? 'bg-blue-600 text-white' : 'bg-red-600 text-white'}`}>
              {isDeveloper ? 'DEV VIP' : isPro ? 'PRO VIP' : isTrialActive ? `PRO (${trialDaysLeft}d prueba)` : 'EXPIRADO'}
            </span>
          </div>
          <p className={`text-[11px] ${isDark ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
            {isPro || isDeveloper 
              ? 'Tienes acceso ilimitado a todas las herramientas avanzadas de IA.' 
              : isTrialActive 
              ? `Estás en tu periodo de prueba ilimitado. Te quedan ${trialDaysLeft} días.` 
              : 'Tu periodo de prueba de 15 días ha finalizado. Suscríbete para continuar.'}
          </p>
          {subscription?.cancel_at_period_end && isPro && <p className={`text-xs ${theme.text}`}>Renovación cancelada. PRO disponible hasta el {new Date(subscription.paid_through * 1000).toLocaleDateString('es-ES')}.</p>}
          {canManage && <>
            <button type="button" disabled={portalBusy} onClick={openPortal} className={`w-full py-3 ${theme.primary} font-black text-[10px] uppercase tracking-widest rounded-full disabled:opacity-50`}>{portalBusy ? 'Abriendo Stripe…' : 'Gestionar suscripción'}</button>
            <p className={`text-[11px] ${theme.muted}`}>En Stripe puedes consultar facturas, cambiar tu método de pago o cancelar la renovación. Entorno de pruebas.</p>
          </>}
          {portalError && <p role="alert" className="text-xs text-red-400">{portalError}</p>}
          {(!isPro && !isDeveloper) && (
            <button 
              onClick={() => { setShowSettingsModal(false); setShowPaywallModal(true); }} 
              className={`w-full py-3 ${theme.primary} font-black text-[10px] uppercase tracking-widest rounded-full hover:scale-105 transition-transform`}
            >
              ⭐ {isTrialActive ? 'Activar Suscripción STUDIO PRO' : 'Renovar STUDIO PRO'}
            </button>
          )}
        </div>

        {/* FORMULARIO DE EDICIÓN MANUAL */}
        <form onSubmit={handleSaveProfileSettings} className="space-y-4">
          <span className={`text-[10px] ${theme.muted} font-bold uppercase tracking-widest block`}>Editar Manualmente</span>

          <div>
            <label className={`text-[10px] ${theme.muted} font-bold uppercase block mb-1`}>Nombre</label>
            <input 
              type="text" 
              value={editProfile?.name || ''} 
              onChange={(e) => setEditProfile({ ...editProfile, name: e.target.value })}
              className={`w-full bg-transparent border-b ${theme.border} ${theme.text} py-2 text-xs font-bold outline-none`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`text-[10px] ${theme.muted} font-bold uppercase block mb-1`}>Peso (kg)</label>
              <input 
                type="number" step="0.1"
                value={editProfile?.peso || ''} 
                onChange={(e) => {
                  const newPeso = e.target.value;
                  const updated = { ...editProfile, peso: newPeso };
                  updated.customMacros = calculateScienceMacros(updated);
                  setEditProfile(updated);
                }}
                className={`w-full bg-transparent border-b ${theme.border} ${theme.text} py-2 text-xs font-bold outline-none`}
              />
            </div>
            <div>
              <label className={`text-[10px] ${theme.muted} font-bold uppercase block mb-1`}>Altura (cm)</label>
              <input 
                type="number" 
                value={editProfile?.altura || ''} 
                onChange={(e) => {
                  const newAlt = e.target.value;
                  const updated = { ...editProfile, altura: newAlt };
                  updated.customMacros = calculateScienceMacros(updated);
                  setEditProfile(updated);
                }}
                className={`w-full bg-transparent border-b ${theme.border} ${theme.text} py-2 text-xs font-bold outline-none`}
              />
            </div>
          </div>

          <div>
            <label className={`text-[10px] ${theme.muted} font-bold uppercase block mb-1`}>Objetivo Principal</label>
            <select 
              value={editProfile?.objetivo || 'Recomposición'} 
              onChange={(e) => {
                const newObj = e.target.value;
                const updated = { ...editProfile, objetivo: newObj };
                updated.customMacros = calculateScienceMacros(updated);
                setEditProfile(updated);
              }}
              className={`w-full bg-transparent border-b ${theme.border} ${theme.text} py-2 text-xs font-bold outline-none`}
            >
              <option value="Perder Grasa" className={theme.card}>Perder Grasa / Definición</option>
              <option value="Recomposición" className={theme.card}>Recomposición Corporal</option>
              <option value="Ganar Músculo" className={theme.card}>Ganar Músculo / Volumen</option>
            </select>
          </div>

          <div>
            <label className={`text-[10px] ${theme.muted} font-bold uppercase block mb-1`}>Tipo de Dieta</label>
            <select 
              value={editProfile?.dieta || 'tradicional'} 
              onChange={(e) => setEditProfile({ ...editProfile, dieta: e.target.value })}
              className={`w-full bg-transparent border-b ${theme.border} ${theme.text} py-2 text-xs font-bold outline-none`}
            >
              <option value="tradicional" className={theme.card}>Tradicional (Sin restricción)</option>
              <option value="vegetariana" className={theme.card}>Vegetariana</option>
              <option value="vegana" className={theme.card}>Vegana</option>
              <option value="ceto" className={theme.card}>Keto / Cetogénica</option>
            </select>
          </div>

          <div className={`p-4 ${theme.secondary} rounded-2xl border ${theme.border} space-y-3`}>
            <div className="flex justify-between items-center border-b border-gray-400/20 pb-2">
              <span className={`text-[10px] font-bold uppercase tracking-widest ${theme.text}`}>Ajuste de Metas</span>
              <button 
                type="button" 
                onClick={handleRecalculateMacrosClick}
                className="text-[9px] font-bold text-blue-500 underline"
              >
                Auto-Calcular
              </button>
            </div>

            {checkMacroContradictions(editProfile) && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-[9px] text-amber-600 dark:text-amber-400 leading-relaxed font-medium">
                {checkMacroContradictions(editProfile)}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <label className={`text-[9px] ${theme.muted} font-bold block mb-1 uppercase`}>Calorías (kcal)</label>
                <input 
                  type="number" 
                  value={editProfile?.customMacros?.cal || calculateScienceMacros(editProfile).cal} 
                  onChange={(e) => setEditProfile({
                    ...editProfile, 
                    customMacros: { ...(editProfile.customMacros || calculateScienceMacros(editProfile)), cal: parseInt(e.target.value) || 0 }
                  })}
                  className={`w-full bg-transparent border-b ${theme.border} ${theme.text} py-1 font-bold outline-none`}
                />
              </div>
              <div>
                <label className={`text-[9px] text-emerald-500 font-bold block mb-1 uppercase`}>Proteína (g)</label>
                <input 
                  type="number" 
                  value={editProfile?.customMacros?.protein || calculateScienceMacros(editProfile).protein} 
                  onChange={(e) => setEditProfile({
                    ...editProfile, 
                    customMacros: { ...(editProfile.customMacros || calculateScienceMacros(editProfile)), protein: parseInt(e.target.value) || 0 }
                  })}
                  className={`w-full bg-transparent border-b ${theme.border} ${theme.text} py-1 font-bold outline-none`}
                />
              </div>
            </div>
          </div>

          <div>
            <label className={`text-[10px] ${theme.muted} font-bold uppercase block mb-2`}>Equipamiento</label>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
              {AVAILABLE_EQUIPMENT_OPTIONS.map((equip) => {
                const selectedList = editProfile?.equipamientoArray || ['mancuernas'];
                const selected = Array.isArray(selectedList) && selectedList.includes(equip.id);
                return (
                  <button
                    key={equip.id}
                    type="button"
                    onClick={() => handleEquipmentToggle(equip.id)}
                    className={`p-2.5 rounded-2xl border text-[10px] font-bold flex items-center justify-center transition-all ${
                      selected 
                        ? isDark ? 'bg-white text-black border-white' : 'bg-black text-white border-black' 
                        : `${theme.secondary} ${theme.border} ${theme.muted}`
                    }`}
                  >
                    <span className="truncate">{equip.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <button type="submit" className={`w-full py-3.5 ${theme.primary} text-[10px] font-black uppercase tracking-widest rounded-full shadow-lg mt-2`}>
            Guardar Ajustes
          </button>
        </form>

        {/* REINICIO DE DATOS Y CERRAR SESIÓN */}
        <button 
          type="button"
          onClick={() => {
            if (window.confirm("⚠️ ¿Estás seguro de que quieres BORRAR TODOS los datos y empezar el reto de 0 desde el Día 1?")) {
              resetAccountProgress(authUser);
              window.location.reload();
            }
          }} 
          className="w-full py-3.5 bg-red-600 text-white font-black text-[10px] uppercase tracking-widest rounded-full shadow-lg hover:bg-red-700 transition-all active:scale-95"
        >
          Reiniciar mi progreso en este dispositivo
        </button>

        <button 
          disabled={signingOut}
          onClick={closeSession}
          className={`w-full py-3 bg-transparent border ${theme.border} ${theme.muted} text-[10px] font-black uppercase tracking-widest rounded-full hover:bg-gray-400 hover:text-white transition-colors`}
        >
          {signingOut ? 'Cerrando sesión…' : 'Cerrar Sesión'}
        </button>
        {sessionError && <p role="alert" className="text-xs text-red-400">{sessionError}</p>}
      </div>
    </div>
  );
}
